import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle, Switch, LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { api } from "../../../lib/api";
import { useShell } from "../../shell/context";

interface CategoryRow {
  key: string;
  locked: boolean;
  /** False when nothing on the sending path reads this preference yet. The row
   *  says so rather than pretending the switch does something. */
  enforced: boolean;
  /** One boolean per channel, keyed by the channel names the server sent. */
  [channel: string]: string | boolean;
}

interface Matrix {
  channels: string[];
  categories: CategoryRow[];
}

/** The channel columns come from the server, so a new one appears here without
 *  a client change. A missing cell reads as on, matching the opt-out default the
 *  server applies to a missing row. */
function cell(row: CategoryRow, channel: string): boolean {
  const value = row[channel];
  return typeof value === "boolean" ? value : true;
}

/** The platform's own notification matrix: category by channel, account level. */
export function AccountMatrixCard() {
  const t = useT();
  const { variant } = useShell();
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api.get<Matrix>("/account/notifications").then(setMatrix);
  }, []);

  function toggle(cat: string, channel: string, val: boolean) {
    setMatrix((m) =>
      m ? { ...m, categories: m.categories.map((c) => (c.key === cat ? { ...c, [channel]: val } : c)) } : m,
    );
    setSaved(false);
  }

  async function save() {
    if (!matrix) return;
    setBusy(true);
    setSaved(false);
    try {
      const prefs = matrix.categories.flatMap((c) =>
        matrix.channels.map((ch) => ({ category: c.key, channel: ch, enabled: cell(c, ch) })),
      );
      const next = await api.put<Matrix>("/account/notifications", { prefs });
      setMatrix(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setBusy(false);
    }
  }

  if (!matrix) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* On the portal's account page the section heading above already
          says this word, so the card does not say it twice. */}
      {variant !== "portal" && (
        <CardHeader>
          <CardTitle>{t("notifications.title")}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">{t("notifications.intro")}</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">{t("notifications.category")}</th>
                {matrix.channels.map((ch) => (
                  <th key={ch} className="w-24 px-3 py-2 text-center font-medium">
                    {t(`notifications.channel.${ch}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.categories.map((c) => (
                <tr key={c.key} className="border-b border-border/60 last:border-0">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-foreground">{t(`notifications.cat.${c.key}`)}</span>
                      {c.locked && <Lock className="size-3 text-muted-foreground" />}
                    </div>
                    <div className="text-xs text-muted-foreground">{t(`notifications.catDesc.${c.key}`)}</div>
                    {!c.enforced && (
                      <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                        {t("notifications.notEnforcedNote")}
                      </div>
                    )}
                  </td>
                  {matrix.channels.map((ch) => (
                    <td key={ch} className="px-3 py-3 text-center">
                      <div className="flex justify-center">
                        <Switch
                          checked={cell(c, ch)}
                          disabled={c.locked}
                          onCheckedChange={(v) => toggle(c.key, ch, v)}
                          aria-label={`${t(`notifications.cat.${c.key}`)} - ${t(`notifications.channel.${ch}`)}`}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="size-3 shrink-0" />
          {t("notifications.lockedNote")}
        </p>

        <Button onClick={save} loading={busy}>
          {saved ? t("common.saved") : t("common.save")}
        </Button>
      </CardContent>
    </Card>
  );
}
